import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { v4 as uuid } from 'uuid'
import 'wicg-inert'

import { mq } from 'marketing-site/src/library/utils'
import { CloseButton } from 'marketing-site/src/mvp/library/elements/CloseButton'
import { IProps } from './index'

export const Modal = ({
  children,
  size = 'medium',
  isOpen = false,
  onClose,
  hasCloseButton = true,
  hasBackdropBlur = false,
}: IProps): React.ReactElement | null => {
  const [node, setNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = document.createElement('div')
    node.id = `modal-${uuid()}`
    document.body.appendChild(node)
    setNode(node)

    return () => {
      node.parentNode?.removeChild(node)
    }
  }, [])

  const handleKeyDown = (e: KeyboardEvent | React.KeyboardEvent) => {
    if (e.key !== 'Escape') return
    onClose?.()
  }

  useEffect(() => {
    const appElement = document.querySelector<HTMLDivElement>('#__next')

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      appElement?.setAttribute('inert', '')
      document.body.classList.add('no-overflow')
    } else {
      window.removeEventListener('keydown', handleKeyDown)
      appElement?.removeAttribute('inert')
      document.body.classList.remove('no-overflow')
    }
  }, [isOpen])

  if (!node) return null

  const dialogClasses = classNames('modal__dialog', {
    [`modal__dialog--size-${size}`]: size,
  })

  const backdropClasses = classNames('modal__backdrop', {
    'modal__backdrop--has-blur': hasBackdropBlur,
  })

  const classes = classNames('modal', 'grid-layout', {
    'modal--is-visible': isOpen,
  })

  return createPortal(
    <>
      <div className={classes} role="dialog" aria-modal="true" aria-hidden={!isOpen}>
        <div className={dialogClasses}>
          {hasCloseButton && <CloseButton onClick={onClose} />}
          <div className="modal__dialog-content">{children}</div>
        </div>
        <div
          className={backdropClasses}
          role="button"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          onClick={onClose}
        />
      </div>

      <style jsx>
        {`
          .modal {
            display: flex;
            position: fixed;
            align-items: center;
            justify-content: center;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            visibility: hidden;
            opacity: 0;
            transition: visibility 0s 0.24s, opacity 0.24s;
            z-index: 500;

            &--is-visible {
              visibility: visible;
              opacity: 1;
              transition: visibility 0s, opacity 0.24s;
            }

            &__dialog {
              display: flex;
              flex-direction: column;
              padding: 2.5rem 1.25rem;
              max-width: 100%;
              max-height: calc(100vh - 3.75rem);
              background-color: var(--paper);
              box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.04), 0px 24px 48px rgba(0, 0, 0, 0.2);
              border-radius: 1.5rem;
              position: relative;
              z-index: 10;
            }

            &__dialog-content {
              overflow: hidden auto;
            }

            :global(.close-button) {
              position: absolute;
              top: 0.5rem;
              right: 0.8rem;
              z-index: 1;
            }

            :global(.close-button svg) {
              width: 16px;
              height: 16px;
            }

            &__backdrop {
              background: transparentize($mvp-slate, 0.2);
              position: fixed;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;

              &--has-blur {
                backdrop-filter: blur(0.75rem);
              }
            }
          }

          @media (${mq.tablet}) {
            .modal {
              :global(.close-button) {
                top: 1.5rem;
                right: 1.5rem;
              }

              :global(.close-button svg) {
                width: 24px;
                height: 24px;
              }

              &__dialog {
                &--size-small {
                  padding: 4rem 2.5rem;
                  width: 38rem;
                }

                &--size-medium {
                  padding: 4rem 3rem;
                  width: 51rem;
                }

                &--size-large {
                  padding: 4rem 3rem;
                  width: 64rem;
                }

                &--size-full-width {
                  padding: 4.5rem 4rem;
                  width: 100%;
                  max-width: 77.5rem;
                }
              }
            }
          }
        `}
      </style>
    </>,
    node,
  )
}
